import React, { useState, useEffect } from 'react';
import { Package, Zap, Shield, Clock, Smartphone, MapPin, Users, Award, MessageCircle, IdCard, Plane, TrendingUp, Lock } from 'lucide-react';
import { countries, translations } from '../data/countries';
import DoodleBackground from '../components/DoodleBackground';
import PWAInstallButton from '../components/PWAInstallPrompt';
import { usePWAInstall } from '../components/usePWAInstall';

const availableLangs = {
  en: 'English', es: 'Espanol', fr: 'Francais', de: 'Deutsch',
  zh: '中文', ja: '日本語', ar: 'العربية', pt: 'Portugues',
  hi: 'हिन्दी', it: 'Italiano', ru: 'Русский', ko: '한국어', th: 'ไทย',
  tr: 'Turkce', pl: 'Polski', uk: 'Українська', ro: 'Romana',
  nl: 'Nederlands', id: 'Bahasa', vi: 'Tieng Viet', bn: 'বাংলা',
  sv: 'Svenska', hu: 'Magyar', cs: 'Cestina', bg: 'Български',
  hr: 'Hrvatski', sr: 'Српски', sw: 'Kiswahili', ms: 'Melayu',
};

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [currentLang, setCurrentLang] = useState('en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { triggerInstall } = usePWAInstall();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-detect user's country by IP geolocation
  useEffect(() => {
    fetch('https://api.country.is/')
      .then(res => res.json())
      .then(data => {
        if (data && data.country) {
          const code = data.country.toLowerCase();
          const match = countries.find(c => c.code === code);
          if (match) {
            setSelectedCountry(match);
            if (translations[match.lang]) {
              setCurrentLang(match.lang);
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleCountryChange = (e) => {
    const country = countries.find(c => c.code === e.target.value);
    setSelectedCountry(country);
    if (translations[country.lang]) {
      setCurrentLang(country.lang);
      setShowLangPicker(false);
    } else {
      setShowLangPicker(true);
    }
  };

  const handleLangSelect = (lang) => {
    setCurrentLang(lang);
    setShowLangPicker(false);
  };

  const usdRates = {
    USD: 1, EUR: 0.92, GBP: 0.79, JPY: 149, CNY: 7.24, INR: 83.5,
    BRL: 4.97, MXN: 17.15, CAD: 1.36, AUD: 1.53, KRW: 1330, CHF: 0.88,
    SEK: 10.5, NOK: 10.7, DKK: 6.87, PLN: 4.02, CZK: 22.5, HUF: 356,
    RON: 4.57, BGN: 1.8, HRK: 6.93, RSD: 108, TRY: 32.5, RUB: 92,
    UAH: 37.5, THB: 35.5, VND: 24500, IDR: 15700, MYR: 4.7, PHP: 56.5,
    SGD: 1.34, HKD: 7.82, TWD: 31.5, NZD: 1.65, ZAR: 18.5, NGN: 1550,
    KES: 153, GHS: 14.5, EGP: 30.9, AED: 3.67, SAR: 3.75, QAR: 3.64,
    KWD: 0.31, BHD: 0.38, OMR: 0.39, JOD: 0.71, ILS: 3.7, PKR: 278,
    BDT: 110, LKR: 312, NPR: 133, ARS: 870, CLP: 930, COP: 3950,
    PEN: 3.72, UYU: 39, MAD: 10, TND: 3.1, DZD: 135, XOF: 605,
    XAF: 605, ISK: 137, ALL: 95, GEL: 2.7, AMD: 387, AZN: 1.7,
    KZT: 450, UZS: 12500, KGS: 89, MNT: 3400, JMD: 155, TTD: 6.78,
    CRC: 515, DOP: 58.5, GTQ: 7.82, HNL: 24.7, NIO: 36.7, BOB: 6.91,
    PYG: 7300, FJD: 2.25, MUR: 45.5, MVR: 15.4, SCR: 13.5, BWP: 13.6,
    NAD: 18.5, ZMW: 26.5, ETB: 56.5, TZS: 2520, UGX: 3800, RWF: 1270,
    MZN: 63.5, AOA: 830, SOS: 571, MMK: 2100, KHR: 4100, LAK: 20800,
    IRR: 42000, IQD: 1310, YER: 250, LBP: 89500, SYP: 13000, CUP: 24,
    BZD: 2, BBD: 2, BSD: 1, GYD: 209, SRD: 36, HTG: 132, PAB: 1,
  };

  const localRate = usdRates[selectedCountry.currency] || 1;
  const localPrice = localRate === 1 ? '1' : Math.round(localRate) > 1 ? Math.round(localRate).toLocaleString() : localRate.toFixed(2);

  const t = translations[currentLang] || translations.en;

  return (
    <div>
      {/* Country Bar */}
      <div className="country-bar" data-testid="country-bar">
        <select data-testid="country-selector" value={selectedCountry.code} onChange={handleCountryChange}>
          {countries.map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
        <span className="country-currency" style={{ fontSize: '11px', color: '#6B7280' }}>
          {selectedCountry.currency} ({selectedCountry.symbol})
        </span>
        <span className="lang-divider" style={{ fontSize: '11px', color: '#7851A9', fontWeight: 600 }}>|</span>
        <select
          data-testid="language-selector"
          className="lang-select"
          value={currentLang}
          onChange={(e) => handleLangSelect(e.target.value)}
          style={{
            border: '1px solid #7851A9',
            borderRadius: '10px',
            padding: '4px 24px 4px 8px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#7851A9',
            background: 'rgba(120,81,169,0.06) url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%237851A9\' stroke-width=\'2\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'/%3E%3C/svg%3E") no-repeat right 6px center',
            appearance: 'none',
            WebkitAppearance: 'none',
            cursor: 'pointer',
          }}
        >
          {Object.entries(availableLangs).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>

      {/* Language notice banner */}
      {showLangPicker && (
        <div data-testid="lang-notice" style={{
          position: 'fixed',
          top: '36px',
          left: 0,
          right: 0,
          zIndex: 199,
          background: 'linear-gradient(135deg, #7851A9, #5B2D8E)',
          color: '#fff',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          fontSize: '13px',
          fontWeight: 500,
        }}>
          <span>Translation not available for {selectedCountry.name}'s language — choose one:</span>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {Object.entries(availableLangs).map(([code, name]) => (
              <button
                key={code}
                data-testid={`lang-btn-${code}`}
                onClick={() => handleLangSelect(code)}
                style={{
                  background: currentLang === code ? '#fff' : 'rgba(255,255,255,0.15)',
                  color: currentLang === code ? '#5B2D8E' : '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '3px 12px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {name}
              </button>
            ))}
          </div>
          <button
            data-testid="lang-notice-close"
            onClick={() => setShowLangPicker(false)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            x
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="/" className="nav-logo">
            <div className="logo-img-wrap">
              <div className="logo-pulse"></div>
              <img src="/logo.png" alt="Spee'V'Dee" />
            </div>
            <span className="logo-text">
              <span className="bk">Spee</span>
              <span className="vv">'V'</span>
              <span className="bk">Dee</span>
            </span>
          </a>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#features" onClick={() => setMobileMenuOpen(false)}>{t.nav_features}</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>{t.nav_how}</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t.nav_pricing}</a>
            <a href="#investors" onClick={() => setMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <TrendingUp size={16} />
              {t.nav_investors}
            </a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>{t.nav_contact}</a>
            <button className="nav-cta" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); triggerInstall(); }}>{t.download_app}</button>
          </div>
          <button className="mobile-menu" data-testid="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? '\u2715' : '\u2630'}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <DoodleBackground />
        <div className="hero-inner">
          <div className="hero-left">
            <div className="hero-badges">
              <span className="hero-badge badge-fast" style={{ cursor: 'pointer' }} onClick={() => document.getElementById('feat-sameday')?.scrollIntoView({ behavior: 'smooth' })}>
                <Zap size={14} /> Fast
              </span>
              <span className="hero-badge badge-secure" style={{ cursor: 'pointer' }} onClick={() => document.getElementById('feat-verified')?.scrollIntoView({ behavior: 'smooth' })}>
                <Shield size={14} /> Secure
              </span>
              <span className="hero-badge badge-track" style={{ cursor: 'pointer' }} onClick={() => document.getElementById('feat-tracking')?.scrollIntoView({ behavior: 'smooth' })}>
                <MapPin size={14} /> Trackable
              </span>
            </div>
            <h1>
              {t.hero_title}
              <br />
              <span className="highlight">{t.hero_subtitle}</span> {t.hero_suffix}
            </h1>
            <p className="hero-slogan" style={{
              fontSize: '18px',
              fontWeight: 700,
              fontStyle: 'italic',
              color: '#7851A9',
              marginTop: '-8px',
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}>
              Wanna send it? Spee'V'Dee it.
            </p>
            <p>
              {t.hero_desc}
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" data-testid="hero-download-app" onClick={triggerInstall}>
                <Zap size={18} />
                {t.download_app}
              </button>
              <button className="btn btn-outline" data-testid="hero-how-it-works" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
                <Package size={18} />
                {t.how_it_works}
              </button>
            </div>
          </div>

          <div className="hero-right">
            <div className="qr-card" style={{ textAlign: 'center' }}>
              <div className="qr-title">{t.scan_qr}</div>
              <a href="https://www.speedyvariety.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=https://www.speedyvariety.com&color=7851A9&bgcolor=FFFFFF"
                  alt="Download QR"
                  className="qr-code"
                  style={{ display: 'block', margin: '0 auto' }}
                />
              </a>
              <div className="qr-subtitle">
                {t.qr_sub}
              </div>
              <div className="qr-device-note">{t.qr_note}</div>
            </div>

            <div className="tier-row">
              <a href="#d2d" className="tier-card t-d2d" data-testid="tier-d2d">
                <div className="tier-icon">🚶</div>
                <h4>{t.tier_d2d}</h4>
                <div className="tier-sub">{t.tier_d2d_sub}</div>
                <div className="tier-time">{t.tier_d2d_time}</div>
              </a>
              <a href="#c2c" className="tier-card t-c2c" data-testid="tier-c2c">
                <div className="tier-icon">🚗</div>
                <h4>{t.tier_c2c}</h4>
                <div className="tier-sub">{t.tier_c2c_sub}</div>
                <div className="tier-time">{t.tier_c2c_time}</div>
              </a>
              <a href="#s2s" className="tier-card t-s2s" data-testid="tier-s2s">
                <div className="tier-icon">🚆</div>
                <h4>{t.tier_s2s}</h4>
                <div className="tier-sub">{t.tier_s2s_sub}</div>
                <div className="tier-time">{t.tier_s2s_time}</div>
              </a>
              <a href="#int" className="tier-card t-int" data-testid="tier-int">
                <div className="tier-icon">✈️</div>
                <h4>{t.tier_int}</h4>
                <div className="tier-sub">{t.tier_int_sub}</div>
                <div className="tier-time">{t.tier_int_time}</div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" data-testid="stats-section">
        <div className="stats-inner">
          <div>
            <div className="stat-num">10K+</div>
            <div className="stat-label">{t.stat_deliveries}</div>
          </div>
          <div>
            <div className="stat-num">195</div>
            <div className="stat-label">{t.stat_countries}</div>
          </div>
          <div>
            <div className="stat-num">4.9</div>
            <div className="stat-label">{t.stat_rating}</div>
          </div>
          <div>
            <div className="stat-num">{selectedCountry.symbol}{localPrice}/mo</div>
            <div className="stat-label">{t.stat_subscription}</div>
          </div>
        </div>
      </section>

      {/* App Preview Phone */}
      <section className="demo" id="install-app" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="section-header">
          <span className="section-tag">{t.dl_tag || 'GET THE APP'}</span>
          <h2>{t.dl_phone_title || "The Spee'V'Dee App"}</h2>
          <p>{t.dl_phone_desc || 'Send packages, become a courier, track deliveries — all from your phone.'}</p>
        </div>
        <div className="demo-inner">
          <div className="demo-phone" data-testid="phone-app-preview" style={{ position: 'relative' }}>
            <div className="demo-notch"></div>
            <iframe
              src="/app/index.html"
              title="Spee'V'Dee App"
              allow="geolocation; camera; microphone"
              allowFullScreen
            ></iframe>
            <div
              data-testid="phone-install-overlay"
              onClick={triggerInstall}
              title="Tap to install Spee'V'Dee"
              style={{ position: 'absolute', inset: 0, cursor: 'pointer', zIndex: 2 }}
            ></div>
          </div>
          <div className="demo-hint" style={{ cursor: 'pointer' }} onClick={triggerInstall}>
            <Smartphone size={16} />
            {t.install_hint || 'Tap here to install on your device'}
          </div>
        </div>
      </section>

      {/* Tier Details */}
      <section className="tiers" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="section-header">
          <span className="section-tag">{t.tiers_tag}</span>
          <h2>{t.tiers_title}</h2>
          <p>{t.tiers_desc}</p>
        </div>
        <div className="tier-detail-grid">
          <div className="tier-detail t-d2d" id="d2d">
            <div className="td-time">
              <Clock size={14} /> {t.tier_d2d_time}
            </div>
            <h3>🚶 {t.tier_d2d}</h3>
            <div className="td-sub">{t.tier_d2d_sub}</div>
            <ul>
              <li>Pickup within 30 minutes</li>
              <li>Delivered within 12 hours</li>
              <li>Live GPS tracking</li>
              <li>Ideal for documents, food, small packages</li>
              <li>In-app chat with your courier</li>
            </ul>
          </div>
          <div className="tier-detail t-c2c" id="c2c">
            <div className="td-time">
              <Clock size={14} /> {t.tier_c2c_time}
            </div>
            <h3>🚗 {t.tier_c2c}</h3>
            <div className="td-sub">{t.tier_c2c_sub}</div>
            <ul>
              <li>Courier travels between cities</li>
              <li>Package inspection at pickup</li>
              <li>Real-time status updates</li>
              <li>Perfect for medium-sized packages</li>
              <li>Proof of delivery photos</li>
            </ul>
          </div>
          <div className="tier-detail t-s2s" id="s2s">
            <div className="td-time">
              <Clock size={14} /> {t.tier_s2s_time}
            </div>
            <h3>🚆 {t.tier_s2s}</h3>
            <div className="td-sub">{t.tier_s2s_sub}</div>
            <ul>
              <li>Covers long-distance routes</li>
              <li>Package insurance included</li>
              <li>Multiple checkpoint updates</li>
              <li>Suitable for larger packages</li>
              <li>Identity verified at every point</li>
            </ul>
          </div>
          <div className="tier-detail t-int" id="int">
            <div className="td-time">
              <Clock size={14} /> {t.tier_int_time}
            </div>
            <h3>✈️ {t.tier_int}</h3>
            <div className="td-sub">{t.tier_int_sub}</div>
            <ul>
              <li>Courier-to-courier handoff system</li>
              <li>Flight details tracking</li>
              <li>Customs-ready documentation</li>
              <li>Multi-country coverage</li>
              <li>Full chain-of-custody tracking</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features" id="features" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="section-header">
          <span className="section-tag">{t.features_tag}</span>
          <h2>{t.features_title}</h2>
          <p>{t.features_desc}</p>
        </div>
        <div className="features-grid">
          <div className="feature-card" id="feat-tracking" data-testid="feat-tracking">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #9B6DC6, #7851A9)' }}>
              <MapPin color="white" />
            </div>
            <h3>{t.feat_tracking}</h3>
            <p>{t.feat_tracking_d}</p>
          </div>
          <div className="feature-card" id="feat-inspect" data-testid="feat-inspect">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #60A5FA, #2563EB)' }}>
              <Package color="white" />
            </div>
            <h3>{t.feat_inspect}</h3>
            <p>{t.feat_inspect_d}</p>
          </div>
          <div className="feature-card" id="feat-sameday" data-testid="feat-sameday">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #34D399, #059669)' }}>
              <Zap color="white" />
            </div>
            <h3>{t.feat_sameday}</h3>
            <p>{t.feat_sameday_d}</p>
          </div>
          <div className="feature-card" id="feat-chat" data-testid="feat-chat">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #F87171, #DC2626)' }}>
              <MessageCircle color="white" />
            </div>
            <h3>{t.feat_chat}</h3>
            <p>{t.feat_chat_d}</p>
          </div>
          <div className="feature-card" id="feat-verified" data-testid="feat-verified">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #FBBF24, #F59E0B)' }}>
              <IdCard color="white" />
            </div>
            <h3>{t.feat_verified}</h3>
            <p>{t.feat_verified_d}</p>
          </div>
          <div className="feature-card" id="feat-fa" data-testid="feat-fa">
            <div className="feature-icon" style={{ background: 'linear-gradient(135deg, #A78BFA, #8B5CF6)' }}>
              <Plane color="white" />
            </div>
            <h3>{t.feat_fa}</h3>
            <p>{t.feat_fa_d}</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="section-header">
          <span className="section-tag">{t.hiw_tag}</span>
          <h2>{t.hiw_title}</h2>
          <p>{t.hiw_desc}</p>
        </div>
        <div className="steps">
          <div className="step">
            <div className="step-num">1</div>
            <h3>{t.step1}</h3>
            <p>{t.step1_d}</p>
          </div>
          <div className="step">
            <div className="step-num">2</div>
            <h3>{t.step2}</h3>
            <p>{t.step2_d}</p>
          </div>
          <div className="step">
            <div className="step-num">3</div>
            <h3>{t.step3}</h3>
            <p>{t.step3_d}</p>
          </div>
          <div className="step">
            <div className="step-num">4</div>
            <h3>{t.step4}</h3>
            <p>{t.step4_d}</p>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="trust" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="trust-inner">
          <div className="section-header">
            <span className="section-tag" style={{ background: 'rgba(255,255,255,.1)', color: '#fff' }}>
              {t.trust_tag}
            </span>
            <h2 style={{ color: '#fff' }}>{t.trust_title}</h2>
            <p style={{ color: 'rgba(255,255,255,.7)' }}>{t.trust_desc}</p>
          </div>
          <div className="trust-grid">
            <div className="trust-card" data-testid="trust-id">
              <div className="t-icon">🪪</div>
              <h3>{t.trust_id}</h3>
              <p>{t.trust_id_d}</p>
            </div>
            <div className="trust-card" data-testid="trust-face">
              <div className="t-icon">🤳</div>
              <h3>{t.trust_face}</h3>
              <p>{t.trust_face_d}</p>
            </div>
            <div className="trust-card" data-testid="trust-pkg">
              <div className="t-icon">📦</div>
              <h3>{t.trust_pkg}</h3>
              <p>{t.trust_pkg_d}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dual CTA */}
      <section className="dual-cta" id="pricing" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="dual-cta-grid">
          <div className="cta-card sender" data-testid="cta-sender">
            <h3>📦 {t.cta_send}</h3>
            <p>{t.cta_send_d}</p>
            <div className="sub-price">
              <Package size={14} /> {selectedCountry.symbol}{localPrice}/month — {t.cta_send_sub}
            </div>
            <ul>
              <li>Door-to-door or international</li>
              <li>{t.feat_tracking}</li>
              <li>{t.feat_verified}</li>
              <li>Proof of delivery photos</li>
            </ul>
            <button className="btn" data-testid="cta-try-btn">
              <Package size={18} />
              {t.cta_try}
            </button>
          </div>
          <div className="cta-card courier" data-testid="cta-courier">
            <h3>🚗 {t.cta_courier}</h3>
            <p>{t.cta_courier_d}</p>
            <div className="sub-price">
              <Users size={14} /> {t.cta_courier_sub}
            </div>
            <ul>
              <li>Earn per delivery</li>
              <li>ID + facial scan to register</li>
              <li>Weekly payouts</li>
              <li>Flexible schedule</li>
            </ul>
            <button className="btn" data-testid="cta-earn-btn">
              <Users size={18} />
              {t.cta_earn}
            </button>
          </div>
        </div>
      </section>

      {/* Investor Portal CTA */}
      <section id="investors" className="trust" style={{ background: 'linear-gradient(135deg, #1F2937, #111827)', position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <div className="trust-inner">
          <div className="section-header">
            <span className="section-tag" style={{ background: 'rgba(255,255,255,.1)', color: '#fff' }}>
              {t.nav_investors || 'FOR INVESTORS'}
            </span>
            <h2 style={{ color: '#fff' }}>Investment Opportunity</h2>
            <p style={{ color: 'rgba(255,255,255,.7)' }}>Join us in revolutionizing global package delivery — by Nighteengale Group</p>
          </div>
          <div className="trust-grid">
            <div className="trust-card">
              <div className="t-icon"><TrendingUp size={28} color="#7851A9" /></div>
              <h3>3-Year Projection</h3>
              <p>Year 1: $5.5M revenue | Year 3: $118.8M with 3M active users. Cumulative net income: $76.5M.</p>
            </div>
            <div className="trust-card">
              <div className="t-icon"><Plane size={28} color="#7851A9" /></div>
              <h3>Flight Attendant Network</h3>
              <p>500,000+ airline crew globally as primary couriers — already background-checked, trusted, and on regular routes.</p>
            </div>
            <div className="trust-card">
              <div className="t-icon"><Shield size={28} color="#7851A9" /></div>
              <h3>NDA-Protected Access</h3>
              <p>Full business model, financials, and strategy available through our secure, NDA-gated investor portal.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <a
              href="/investor-portal"
              className="btn btn-primary"
              data-testid="investor-portal-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}
            >
              <Lock size={18} />
              Access Investor Portal
            </a>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,.5)', marginTop: '12px' }}>
              Requires NDA acceptance & email verification
            </p>
          </div>
        </div>
      </section>

      {/* Download */}
      <section className="download" id="download" style={{ position: 'relative', overflow: 'hidden' }}>
        <DoodleBackground />
        <h2>{t.dl_title}</h2>
        <p>{t.dl_desc}</p>
        <div className="download-qr">
          <div className="download-qr-card" style={{ textAlign: 'center' }}>
            <a href="https://www.speedyvariety.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
              <img
                src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://www.speedyvariety.com&color=FFFFFF&bgcolor=5B2D8E&format=png"
                alt="Scan to install"
                style={{ display: 'block', margin: '0 auto' }}
              />
            </a>
            <p>{t.qr_sub}</p>
          </div>
          <div className="store-buttons" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <PWAInstallButton />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="nav-logo">
              <img src="/logo.png" alt="Spee'V'Dee" />
              <span className="logo-text">
                <span className="bk">Spee</span>
                <span className="vv">'V'</span>
                <span className="bk">Dee</span>
              </span>
            </div>
            <p>Fast peer-to-peer package delivery app — worldwide. Ship anything, anywhere with confidence.</p>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <a href="/about">About Us</a>
            <a href="/business-model-canvas" data-testid="footer-bmc-link">Business Model Canvas</a>
            <a href="/investor-portal">Investor Portal</a>
            <a href="mailto:careers@speedyvariety.com">Careers</a>
            <a href="mailto:press@speedyvariety.com">Press Kit</a>
          </div>
          <div className="footer-col">
            <h4>Support</h4>
            <a href="/safety">Safety</a>
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="mailto:support@speedyvariety.com">Contact Support</a>
          </div>
          <div className="footer-col">
            <h4>Connect</h4>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter / X</a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">Instagram</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer">TikTok</a>
          </div>
        </div>
        <div className="footer-bottom">
          &copy; 2025 Spee'V'Dee. All rights reserved. Made with love worldwide.
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
